import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import { DemoUnavailable } from "@/components/demo-unavailable/DemoUnavailable.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  useToasts,
  Button,
  Input,
  LinkTo,
  Skeleton,
  Stack,
  Switch,
  TabItem,
  Tabs,
  Text,
  Textarea,
  TextTruncated,
  Title,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { SettingsIntegrationEmployees } from "@/pages/settings/integrations/components/SettingsIntegrationEmployees.tsx";
import { SettingsIntegrationEmployeesActions } from "@/pages/settings/integrations/components/SettingsIntegrationEmployeesActions.tsx";
import { SettingsIntegrationNotFoundBanner } from "@/pages/settings/integrations/components/SettingsIntegrationNotFoundBanner.tsx";
import { SettingsIntegrationTabs } from "@/pages/settings/integrations/settings-integration.types.ts";
import {
  useIntegrationDisconnect,
  useIntegrationGoogleCalendarServiceAccount,
  useIntegrationGoogleCalendarServiceAccountUpdate,
} from "@/queries/integrations/use-integrations.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { AppDistribution, ColumnSizes } from "@/types/common.types.ts";
import {
  ApiGoogleCalendarEmployee,
  ApiIntegrationGoogleCalendarServiceAccountSave,
  ApiIntegrationType,
} from "@/types/integrations/integration.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { createColumnHelper } from "@tanstack/react-table";
import { parseInt } from "lodash";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import { z } from "zod";

interface Props {
  tab: SettingsIntegrationTabs;
}

export const SettingsIntegrationGoogleCalendar = ({ tab }: Props) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { showInfo } = useToasts();
  const { openModal } = useModals();
  const navigate = useNavigate();
  const disconnect = useIntegrationDisconnect();

  useTitle(
    t("integrations:google_calendar_title"),
    t("integrations:settings_integrations_title"),
    t("common:settings")
  );

  const { data: integration, isLoading } = useIntegrationGoogleCalendarServiceAccount(
    id ? parseInt(id, 10) : undefined
  );

  const isEmployeesTab = tab === SettingsIntegrationTabs.employees;

  const handleTabChanged = (tabIndex: number) => {
    const urls = [
      `/settings/integrations/${id}/google-calendar-service-account`,
      `/settings/integrations/${id}/google-calendar-service-account/employees`,
    ];
    navigate(urls[tabIndex]);
  };

  const accountColumns = useMemo(() => {
    const columnHelper = createColumnHelper<ApiGoogleCalendarEmployee>();
    return [
      columnHelper.display({
        id: "calendar_email",
        header: t("integrations:google_calendar_email"),
        cell: (cell) => <TextTruncated title={cell.row.original.calendar_email ?? "-"} />,
        ...ColumnSizes.middle,
      }),
    ];
  }, [t]);

  const SettingsIntegrationGoogleCalendarSchemaObject = z.object({
    is_active: z.boolean(),
    identity: z.string().nullable().optional(),
    credentials: z.string().nullable().optional(),
    sync_out_of_office_events: z.boolean().optional(),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(SettingsIntegrationGoogleCalendarSchemaObject),
    values: {
      is_active: integration?.is_active || false,
      credentials: integration?.credentials,
      identity: integration?.identity,
      sync_out_of_office_events: integration?.sync_out_of_office_events,
    },
  });

  const isActive = watch("is_active");

  const manageIntegrationGoogleCalendar = useIntegrationGoogleCalendarServiceAccountUpdate(control);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.integrations.manageViewed, {
      [analyticProperties.source]: "Google Calendar",
    });
  });

  const onSubmit = handleSubmit(async (schema) => {
    if (!integration) return;
    const payload: ApiIntegrationGoogleCalendarServiceAccountSave = {
      is_active: schema.is_active,
      credentials: schema.credentials,
      identity: schema.identity || null,
      sync_out_of_office_events: schema.sync_out_of_office_events,
    };

    try {
      await manageIntegrationGoogleCalendar.mutateAsync({ id: integration.id, payload });
      analyticsService.trackEvent(analyticEvents.integrations.edited, {
        [analyticProperties.source]: "Google Calendar",
      });

      showInfo({ title: t("common:you_changes_were_saved") });
    } catch {
      return;
    }
  });

  const handleDeleteChannel = async () => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("integrations:disconnect_confirm"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        if (!integration) return;
        const result = await disconnect.mutateAsync(integration.id);

        if (result?.success) {
          analyticsService.trackEvent(analyticEvents.integrations.disconnected, {
            [analyticProperties.actionType]: "Google Calendar",
          });
          navigate(`/settings/integrations`);
        }
      },
    });
  };

  return (
    <Page className={isEmployeesTab ? "h-full" : undefined}>
      <Page.Header
        title={t("integrations:google_calendar_title")}
        showBack={true}
        navigateTo={"/settings/integrations"}
      >
        {integration && (
          <SettingsIntegrationEmployeesActions
            integrationId={integration.id}
            integrationType={ApiIntegrationType.google_calendar_service_account}
            isActive={integration.is_active}
            isEmployeesTab={isEmployeesTab}
          />
        )}
      </Page.Header>
      <Page.Content stretch={isEmployeesTab}>
        <Stack className={isEmployeesTab ? "px-16 flex-1 min-h-0 min-w-0" : "px-16"}>
          <Tabs
            fill={isEmployeesTab}
            onActiveTabChange={handleTabChanged}
            activeTab={isEmployeesTab ? 1 : 0}
          >
            <TabItem title={t("common:settings")}>
              {integration?.is_active && (
                <SettingsIntegrationNotFoundBanner
                  integrationId={integration.id}
                  integrationType={ApiIntegrationType.google_calendar_service_account}
                  notFoundEmployeesCount={integration.not_found_employees_count}
                />
              )}
              <Stack gap={"xl"} className={"container-sm mx-auto"}>
                {isLoading && <Skeleton className={"w-full h-24"} />}
                <form onSubmit={onSubmit} className={"flex flex-col grow"}>
                  <Title
                    size={"md"}
                    header={t("integrations:google_calendar_install_overview")}
                    paddingBottom
                    paddingTop
                  />
                  <Stack gap={"md"}>
                    <Text className={"whitespace-pre-line"}>
                      {t("integrations:google_calendar_install_description")}
                    </Text>
                    {import.meta.env.VITE_APP_DISTRIBUTION == AppDistribution.skailer && (
                      <Text>
                        {t("integrations:google_calendar_install_help")}{" "}
                        <LinkTo target={"_blank"} to={"/help/article/google-calendar-integration"}>
                          {t("integrations:google_calendar_install_help_link")}
                        </LinkTo>
                        .
                      </Text>
                    )}
                  </Stack>
                  <Title header={t("integrations:manage")} paddingBottom paddingTop />
                  <Stack gap={"sm"}>
                    <Controller
                      render={({ field: { value, onChange } }) => (
                        <Switch
                          checked={value}
                          onChange={(newValue) => {
                            onChange(newValue);
                          }}
                          size={"sm"}
                          title={t("integrations:google_calendar_active_switch")}
                          description={t("integrations:google_calendar_active_switch_description")}
                        />
                      )}
                      control={control}
                      name={"is_active"}
                    />
                    {isActive && (
                      <>
                        <Input
                          label={t("integrations:google_calendar_identity")}
                          type={"text"}
                          {...register("identity")}
                          error={errors.identity?.message}
                        />
                        <Textarea
                          maxHeight={300}
                          label={t("integrations:google_calendar_credentials")}
                          error={errors.credentials?.message}
                          {...register("credentials")}
                        ></Textarea>
                      </>
                    )}
                  </Stack>
                  {isActive && (
                    <>
                      <Title header={t("common:settings")} paddingBottom paddingTop />
                      <Stack gap={"sm"}>
                        <Controller
                          render={({ field: { value, onChange } }) => (
                            <Switch
                              checked={value}
                              onChange={(newValue) => {
                                onChange(newValue);
                              }}
                              size={"sm"}
                              title={t(
                                "integrations:google_calendar_sync_out_of_office_events_title"
                              )}
                              description={t(
                                "integrations:google_calendar_sync_out_of_office_events_description"
                              )}
                            />
                          )}
                          control={control}
                          name={"sync_out_of_office_events"}
                        />
                      </Stack>
                    </>
                  )}
                  <ServerErrorField errors={errors} />
                </form>
                <DeleteListItem
                  onDelete={handleDeleteChannel}
                  title={t("integrations:disconnect")}
                />

                <DemoUnavailable className="w-full">
                  <Button
                    onClick={onSubmit}
                    isLoading={manageIntegrationGoogleCalendar.isPending}
                    size={"lg"}
                    className={"w-full"}
                    type={"submit"}
                  >
                    {t("common:save")}
                  </Button>
                </DemoUnavailable>
              </Stack>
            </TabItem>
            <TabItem title={t("core:employees")} className={"flex min-h-0 min-w-0 flex-1 flex-col"}>
              {integration && (
                <SettingsIntegrationEmployees
                  integrationId={integration.id}
                  integrationType={ApiIntegrationType.google_calendar_service_account}
                  accountColumns={accountColumns}
                />
              )}
            </TabItem>
          </Tabs>
        </Stack>
      </Page.Content>
    </Page>
  );
};
